import * as React from "react";
import * as ReactDOM from "react-dom";
import classNames from "classnames";
import ReactTable from "react-table";
import "react-table/react-table.css";

import { IconChevronLeft, IconChevronRight } from "../../components/icons";
import { Spinner } from ".";
import { IconButton } from "../buttons";
import { fitWidth, keepWidest, ResizedColumn, withFittedWidths, withResized } from "./dataTableFit";

interface IProps {
	className?: string;
	filterable?: boolean;
	TableComponent?: React.ReactType;
	columns?: any[];
	data?: any[];
	getTableProps?: (...args: any[]) => {};
	getTheadFilterThProps?: (...args: any[]) => {};
	getTheadGroupProps?: (...args: any[]) => {};
	getTheadProps?: (...args: any[]) => {};
	getTheadThProps?: (...args: any[]) => {};
	getTbodyProps?: (...args: any[]) => {};
	getTrProps?: (...args: any[]) => {};
	getTdProps?: (...args: any[]) => {};
	minRows?: number;
	/** rows per page when the user hasn't picked one (default 20) */
	defaultPageSize?: number;
	hasActions?: boolean;
	resizable?: boolean;
	sortable?: boolean;
	defaultSorted?: any[];
	filtered?: any[];
	onFilteredChange?: any;
	noDataText?: string;
	showPagination?: boolean;
	onPageChange?: (pageIndex: number) => void;
	storageKey?: string;
	page?: number;
	// handles clicks on each data table row, if not provided nothing happens
	onRowClick?: ({ row, original }: { row?: any; original?: any }) => void;
	loading?: boolean;
	onDataFiltered?: (data: any) => void;
}

interface IState {
	defaultGridPageSize: number;
	sortingFromStorage: any[];
	/** auto-fit minWidth per column id, from measuring the rendered cells */
	fitted: Record<string, number>;
	/** columns the user dragged to a width; these win over auto-fit */
	resized: ResizedColumn[];
}

class DataTable extends React.Component<IProps, IState> {
	private reactTable: React.RefObject<any>;

	constructor(props: IProps) {
		super(props);
		this.reactTable = React.createRef();

		// Retrieve the default page size
		let pageSizeLen = this.props.defaultPageSize ?? 20;
		if (this.props.storageKey) {
			const storageValue = localStorage.getItem(
				this.props.storageKey + "PageSize"
			);

			if (storageValue && storageValue.length > 0) {
				const storage = JSON.parse(storageValue);
				const { pageSize } = storage;

				if (pageSize) {
					pageSizeLen = pageSize;
				}
			}
		}

		let sorting = this.props.defaultSorted
			? this.props.defaultSorted
			: [
					{
						id: "id",
						desc: true,
					},
			  ];
		if (this.props.storageKey) {
			const storageValue = localStorage.getItem(
				this.props.storageKey + "Sorting"
			);

			if (storageValue && storageValue.length > 0) {
				const storage = JSON.parse(storageValue);
				const { newSorted } = storage;

				if (newSorted) {
					sorting = newSorted;
				}
			}
		}

		let resized: ResizedColumn[] = [];
		if (this.props.storageKey) {
			try {
				resized = JSON.parse(localStorage.getItem(this.props.storageKey + "Resized") ?? "[]");
			} catch {
				resized = [];
			}
		}

		this.state = {
			defaultGridPageSize: pageSizeLen,
			sortingFromStorage: sorting ? sorting : [],
			fitted: {},
			resized,
		};
	}

	private measureFrame = 0;

	public componentDidMount() {
		this.scheduleFit();
	}

	public componentDidUpdate() {
		this.scheduleFit();
	}

	public componentWillUnmount() {
		cancelAnimationFrame(this.measureFrame);
	}

	private scheduleFit() {
		cancelAnimationFrame(this.measureFrame);
		this.measureFrame = requestAnimationFrame(this.fitColumns);
	}

	/**
	 * Auto-fit: size each column to its widest rendered cell (content on one
	 * line) and its longest header word (titles wrap), within fitWidth's
	 * bounds. The dt-measuring class (src/scss/driveable/_reacttable.scss)
	 * switches cells to their intrinsic widths for one synchronous read, so
	 * nothing paints in that state.
	 */
	/** Intrinsic width per column id (see dt-measuring), optionally one column only. */
	private measureColumns(root: HTMLElement, onlyId?: string) {
		const scope = onlyId ? `[data-col="${CSS.escape(onlyId)}"]` : "[data-col]";
		root.classList.add("dt-measuring");
		const measured: Record<string, number> = {};
		const ids: string[] = [];
		root.querySelectorAll<HTMLElement>(`.rt-thead.-header .rt-th${scope}, .rt-tbody .rt-td${scope}`).forEach((cell) => {
			const id = cell.dataset.col!;
			if (cell.classList.contains("rt-th")) ids.push(id);
			measured[id] = Math.max(measured[id] ?? 0, cell.getBoundingClientRect().width);
		});
		root.classList.remove("dt-measuring");
		return { measured, ids };
	}

	private fitColumns = () => {
		if (this.props.loading) return;
		const root = ReactDOM.findDOMNode(this) as HTMLElement | null;
		if (!root) return;

		const { measured, ids } = this.measureColumns(root);
		// the actions column keeps its fixed has-actions width
		if (this.props.hasActions !== false) delete measured[ids[ids.length - 1]];

		const next: Record<string, number> = {};
		for (const [id, width] of Object.entries(measured)) next[id] = fitWidth(width);
		const fitted = keepWidest(this.state.fitted, next);
		if (Object.keys(fitted).some((id) => fitted[id] !== this.state.fitted[id])) {
			this.setState({ fitted });
		}
	};

	/** Double-click a resize handle: size that column to all of its content (no cap). */
	private fitColumnToContent = (id: string) => {
		const root = ReactDOM.findDOMNode(this) as HTMLElement | null;
		if (!root) return;
		const width = this.measureColumns(root, id).measured[id];
		if (width) this.onResizedChange(withResized(this.state.resized, id, width));
	};

	private getResizerProps = (_state: any, _row: any, column: any) => ({
		title: "Drag to resize, double-click to fit",
		onDoubleClick: () => this.fitColumnToContent(column.id),
	});

	public onResizedChange = (resized: ResizedColumn[]) => {
		this.setState({ resized });
		if (this.props.storageKey) {
			localStorage.setItem(this.props.storageKey + "Resized", JSON.stringify(resized));
		}
	};

	/** Tag cells with their column id (for auto-fit) and title any cell that still clips. */
	private cellProps =
		(inner: (...args: any[]) => any) =>
		(state: any, rowInfo: any, column: any) => ({
			...inner(state, rowInfo, column),
			"data-col": column?.id,
			onMouseEnter: (e: React.MouseEvent<HTMLElement>) => {
				const cell = e.currentTarget;
				if (!cell.title && cell.scrollWidth > cell.clientWidth) {
					cell.title = cell.innerText.trim();
				}
			},
		});

	public onPageChange = (e: any, callback: () => void) => {
		// TODO: Find a better way to animate the page
		window.scrollTo({
			top: 0,
			left: 0,
			behavior: "smooth",
		});
		callback();
	};

	public onPageSizeChange = (pageSize: number) => {
		if (this.props.storageKey) {
			if (pageSize && pageSize > 0) {
				localStorage.setItem(
					this.props.storageKey + "PageSize",
					JSON.stringify({
						pageSize: pageSize,
					})
				);
			}
		}
	};

	public getTrProps = (_: any, rowInfo: any) => {
		const { onRowClick } = this.props;
		if (onRowClick) {
			return {
				className: "tr cursor-pointer",
				onClick(e: any, handleOriginal: any) {
					onRowClick(rowInfo);
					if (handleOriginal) {
						handleOriginal();
					}
				},
			};
		} else {
			return {
				className: "tr ",
			};
		}
	};

	public onSortChange = (newSorted: any[]) => {
		if (this.props.storageKey) {
			localStorage.setItem(
				this.props.storageKey + "Sorting",
				JSON.stringify({
					newSorted,
				})
			);
		}
	};

	public render() {
		const {
			className,
			filterable,
			hasActions,
			TableComponent,
			columns,
			data,
			showPagination = true,
			getTableProps = () => {
				return { className: "table" };
			},
			getTheadFilterThProps = () => {
				return { className: "th" };
			},
			getTheadGroupProps = () => {
				return { className: "thead theadgroup" };
			},
			getTheadProps = () => {
				return { className: "thead" };
			},
			getTrProps = () => {
				return { className: "tr" };
			},
			getTheadThProps = () => {
				return { className: "th" };
			},
			getTbodyProps = () => {
				return { className: "tbody" };
			},
			getTdProps = () => {
				return { className: "td" };
			},
			minRows = 0,
			resizable = true,
			sortable = true,
			defaultSorted = [
				{
					id: "id",
					desc: true,
				},
			],
			noDataText,
			filtered,
			onFilteredChange,
			onPageChange,
			page,
			loading,
		} = this.props;
		return (
			<ReactTable
				className={classNames(
					"data-table",
					hasActions !== false && "has-actions",
					className
				)}
				filterable={filterable}
				onFetchData={() => {
					if (this.reactTable.current) {
						const filteredData =
							this.reactTable.current.getResolvedState().sortedData;
						if (this.props.onDataFiltered) {
							this.props.onDataFiltered(filteredData);
						}
					}
				}}
				onFilteredChange={this.props.onFilteredChange}
				filtered={filtered}
				defaultFilterMethod={(filter, row) => {
					return String(row[filter.id])
						.toLowerCase()
						.includes(String(filter.value).toLowerCase());
				}}
				TableComponent={TableComponent}
				columns={withFittedWidths(columns ?? [], this.state.fitted)}
				data={data}
				showPagination={showPagination}
				getTableProps={getTableProps}
				getTheadFilterThProps={getTheadFilterThProps}
				getTheadGroupProps={getTheadGroupProps}
				getTheadProps={getTheadProps}
				getTheadThProps={this.cellProps(getTheadThProps)}
				getTbodyProps={getTbodyProps}
				getTrProps={this.getTrProps}
				getTdProps={this.cellProps(getTdProps)}
				minRows={minRows}
				resizable={resizable}
				resized={this.state.resized}
				onResizedChange={this.onResizedChange}
				getResizerProps={this.getResizerProps}
				loading={loading}
				LoadingComponent={() =>
					loading ? <Spinner className={"spinner-large"} /> : null
				}
				sortable={sortable}
				onSortedChange={this.onSortChange}
				defaultSorted={
					this.state.sortingFromStorage
						? this.state.sortingFromStorage
						: defaultSorted
				}
				PreviousComponent={(p) => (
					<IconButton
						onClick={(e) => this.onPageChange(e, p.onClick)}
						disabled={p.disabled}
						className="btn-link"
					>
						<IconChevronLeft />
					</IconButton>
				)}
				NextComponent={(p) => (
					<IconButton
						onClick={(e) => this.onPageChange(e, p.onClick)}
						disabled={p.disabled}
						className="btn-link"
					>
						<IconChevronRight />
					</IconButton>
				)}
				onPageChange={onPageChange}
				defaultPageSize={this.state.defaultGridPageSize}
				onPageSizeChange={this.onPageSizeChange}
				noDataText={!loading && noDataText}
				page={page}
				ref={this.reactTable}
			/>
		);
	}
}

export default DataTable;
